/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      setTimeout(() => {
        this.target = this.dataset.target;
        this.buttons = Array.from(this.querySelectorAll('button'));
        this.collectionContainer = this.closest('.collection-tabs');
        this.sectionRoot = this.closest('.shopify-section') || this.closest('.section-collection-tabs');
        this.slider = document.getElementById(this.target);
        this.progressBar = this.sectionRoot?.querySelector('.flickity-progress');
        this.repairPanel = this.sectionRoot?.querySelector('[data-repair-tab-panel]');
        this.repairConfigurator = document.querySelector('[data-collection-tabs-embed]');

        if (this.repairPanel && this.repairConfigurator) {
          this.repairPanel.append(this.repairConfigurator);
          // P1 F-009 (26.09.2026): Eine Adresse mit Reparatur-Bezug (?brand=, Sprungmarke in den
          // Konfigurator, Bestaetigung einer Reparaturanfrage) oeffnet direkt den Tab "Reparieren" -
          // ohne resetFlow, damit Markenwahl und Anfrage-Bestaetigung erhalten bleiben, die der
          // Konfigurator beim Laden selbst setzt (wie auf /collections/reparieren).
          if (this.hasRepairIntent()) {
            this.activateRepairTab();
            this.alignInitialRepairView();
          } else {
            this.repairConfigurator.hidden = true;
          }
        }

        this.buttons.forEach((button) => {
          button.addEventListener('click', (event) => {
            const handle = button.dataset.collection;

            this.buttons.forEach((tab) => tab.classList.remove('active'));
            button.classList.add('active');

            if (handle === 'reparieren' && this.repairPanel) {
              this.showRepairConfigurator();
            } else if (handle) {
              this.showProductCollection(handle);
            }

            event.preventDefault();
          });
        });

        if (window.Shopify?.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const index = this.buttons.indexOf(event.target);
            if (index >= 0) this.buttons[index].dispatchEvent(new Event('click'));
          });
        }
      }, 20);
    }

    showRepairConfigurator() {
      if (!this.repairPanel || !this.repairConfigurator) {
        window.location.assign('/collections/reparieren');
        return;
      }

      if (typeof this.repairConfigurator.resetFlow === 'function') {
        this.repairConfigurator.resetFlow();
      }
      this.showRepairPanel();
      // P1 F-009 (26.09.2026): Nach dem Tipp auf "Reparieren" nur so weit scrollen, dass die
      // Markenwahl sichtbar ist (vorher aenderte sich mobil nur die Tabfarbe). Nutzt die
      // Schrittausrichtung des Konfigurators; ist die Markenwahl schon sichtbar, passiert nichts.
      const configurator = this.repairConfigurator;
      if (typeof configurator.alignStepViewport === 'function' && configurator.brandPanel) {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.requestAnimationFrame(() => configurator.alignStepViewport(configurator.brandPanel, 1, { smooth }));
      }
    }

    showRepairPanel() {
      this.slider.hidden = true;
      if (this.progressBar) this.progressBar.hidden = true;
      this.collectionContainer?.classList.add('is-repair-active');
      this.repairPanel.hidden = false;
      this.repairConfigurator.hidden = false;
      // Solange der Produkt-Slider verborgen ist, misst Flickity ihn falsch (z. B. nach dem Drehen
      // des Handys): beim naechsten Wechsel zurueck neu vermessen.
      this.sliderNeedsResize = true;
    }

    hasRepairIntent() {
      if (this.repairConfigurator.querySelector('.repair-inquiry__notification')) return true;
      const url = new URL(window.location.href);
      return url.searchParams.has('brand') || this.hashTargetsConfigurator(url);
    }

    hashTargetsConfigurator(url) {
      if (url.hash.length < 2) return false;
      let id = url.hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch (error) {
        return false;
      }
      const target = document.getElementById(id);
      return Boolean(target && this.repairConfigurator.contains(target));
    }

    activateRepairTab() {
      const repairButton = this.buttons.find((button) => button.dataset.collection === 'reparieren');
      if (repairButton) {
        this.buttons.forEach((tab) => tab.classList.remove('active'));
        repairButton.classList.add('active');
      }
      this.showRepairPanel();
    }

    // Das Umhaengen in den Tab-Bereich trennt den Konfigurator kurz vom Dokument; dabei verwirft
    // er seine geplante Anfangsausrichtung (Bestaetigung bzw. Sprungmarke). Sie wird hier nach dem
    // Laden nachgeholt - mit der Schrittausrichtung des Konfigurators, ohne Animation und nur,
    // solange der Kunde noch nicht selbst gescrollt oder getippt hat. Beim Neuladen und bei
    // Zurueck/Vor stellt der Browser die gemerkte Stelle wieder her (wie im Verkaufen-Bereich).
    alignInitialRepairView() {
      const configurator = this.repairConfigurator;
      if (typeof configurator.alignStepViewport !== 'function') return;
      const entry = typeof performance.getEntriesByType === 'function' ? performance.getEntriesByType('navigation')[0] : null;
      if (entry && (entry.type === 'reload' || entry.type === 'back_forward')) return;
      const events = ['pointerdown', 'touchstart', 'wheel', 'keydown', 'click', 'input'];
      let cancelled = false;
      const cancel = () => { cancelled = true; };
      events.forEach((type) => window.addEventListener(type, cancel, { capture: true, passive: true }));
      const align = () => {
        events.forEach((type) => window.removeEventListener(type, cancel, { capture: true }));
        if (cancelled || configurator.hidden) return;
        const notice = configurator.querySelector('.repair-inquiry__notification');
        if (notice) {
          configurator.alignStepViewport(notice, 1, { smooth: false });
          notice.focus({ preventScroll: true });
          return;
        }
        const step = Number(configurator.activeStep) || 1;
        const target = step === 1 ? configurator.brandPanel : configurator.querySelector(`[data-flow-step="${step}"]`);
        if (target) configurator.alignStepViewport(target, step, { smooth: false });
      };
      const settle = () => {
        let previousY = window.scrollY;
        let stableFrames = 0;
        const tick = () => {
          stableFrames = Math.abs(window.scrollY - previousY) <= 0.5 ? stableFrames + 1 : 0;
          previousY = window.scrollY;
          if (stableFrames < 3) {
            window.requestAnimationFrame(tick);
            return;
          }
          align();
        };
        window.requestAnimationFrame(tick);
      };
      if (document.readyState === 'complete') settle();
      else window.addEventListener('load', settle, { once: true });
    }

    showProductCollection(handle) {
      if (this.repairConfigurator) {
        if (typeof this.repairConfigurator.resetFlow === 'function') {
          this.repairConfigurator.resetFlow();
        }
        this.repairConfigurator.hidden = true;
      }
      if (this.repairPanel) this.repairPanel.hidden = true;
      this.collectionContainer?.classList.remove('is-repair-active');
      this.clearRepairUrl();

      this.slider.hidden = false;
      if (this.progressBar) this.progressBar.hidden = false;
      this.toggleCollection(handle);
    }

    clearRepairUrl() {
      if (!this.repairConfigurator) return;
      const url = new URL(window.location.href);
      // Nach einer Reparaturanfrage steht ?contact_posted=true#<Anfrageformular> in der Adresse;
      // bliebe sie stehen, oeffnete Neuladen oder Zurueck wieder die alte Meldung.
      const inquiryPosted = url.searchParams.has('contact_posted')
        && Boolean(this.repairConfigurator.querySelector('.repair-inquiry__notification'));
      const configuratorHash = this.hashTargetsConfigurator(url);
      if (!url.searchParams.has('brand') && !inquiryPosted && !configuratorHash) return;

      url.searchParams.delete('brand');
      if (inquiryPosted) url.searchParams.delete('contact_posted');
      if (configuratorHash) url.hash = '';
      window.history.replaceState({}, '', url);
    }

    toggleCollection(handle) {
      const products = this.slider.querySelectorAll(`.columns:not([data-collection*="${handle}"])`);
      const activeProducts = this.slider.querySelectorAll(`[data-collection*="${handle}"]`);
      const flkty = Flickity.data(this.slider);
      if (this.sliderNeedsResize) {
        this.sliderNeedsResize = false;
        flkty?.resize();
      }

      products.forEach((product) => {
        product.classList.remove('carousel__slide');
        this.slider.append(product);
      });
      activeProducts.forEach((product) => {
        if (product.dataset.collection === handle) {
          product.classList.add('carousel__slide');
        } else {
          product.classList.remove('carousel__slide');
          this.slider.append(product);
        }
      });
      flkty.insert(activeProducts);
      flkty.reloadCells();
      flkty.select(0, 0, 1);
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
}
